import React, { Suspense, lazy, useEffect, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { TYPOGRAM_SOCIAL } from "../../constants/socialLinks";
import { EXAM_LANDING_PAGES } from "../../data/examLandingData";
import { AnimatePresence } from "framer-motion";
import { FiTarget, FiZap, FiAward, FiTrendingUp, FiBookOpen, FiBriefcase, FiPlayCircle, FiUsers, FiEdit3, FiFileText } from "react-icons/fi";
import AuthService from "../../service/auth.service";
import CourseDataService from "../../service/course.service";
import FeedbackService from "../../service/feedback.service";
import NewsletterService from "../../service/newsletter.service";
import { useFeatureFlags } from "../../context/FeatureFlagContext";
import SubscriptionExtendBanner from "../common/SubscriptionExtendBanner";
import HomeWorkflowShowcase from "../common/HomeWorkflowShowcase";
import ExamPrepDiscovery from "../common/ExamPrepDiscovery";
import { EXAM_PREP_PATH } from "../../constants/examPrep";
import {trackMockFunnel} from "../../utils/mockFunnel";
import { useGuidedTour } from "../common/tours/GuidedTourProvider";
import ExamTypingGuidesBlock from "../seo/ExamTypingGuidesBlock";
import { Rocket } from "lucide-react";

const YouTubeVideos = lazy(() => import("../common/Youtube"));
const FeedbackForm = lazy(() => import("../common/FeedbackForm"));
const TelegramJoinModal = lazy(() => import("../common/TelegramJoinModal"));
const TypingCourseRulesModal = lazy(() => import("./TypingCourseRulesModal"));

// ============ ANIMATIONS ============
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(2deg); }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(139, 92, 246, 0.3); }
  50% { box-shadow: 0 0 40px rgba(139, 92, 246, 0.6), 0 0 60px rgba(59, 130, 246, 0.3); }
`;

// const typewriter = keyframes` // Unused
//   0%, 100% { width: 0; }
//   50% { width: 100%; }
// `;

const scrollBounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(10px); }
`;

const journeyMarquee = keyframes`
  from { transform: translateX(0); }
  to {
    transform: translateX(calc(
      -1 * var(--journey-items) * (var(--journey-card-width) + var(--journey-gap))
    ));
  }
`;

// ============ STYLED COMPONENTS ============
const PageWrapper = styled.div`
  background: #050507;
  overflow-x: hidden;
  scroll-behavior: smooth;
  margin: 0;
  padding: 0;
  min-height: 100vh;
  position: relative;
  width: 100%;
  
  * {
    box-sizing: border-box;
  }
`;

const Section = styled.section`
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  margin: 0;
  padding: 0;

  @media (max-width: 768px) {
    min-height: auto;
    padding: 60px 1rem;
  }
`;

// ============ HERO SECTION ============
const HeroSection = styled(Section)`
  background: radial-gradient(ellipse 150% 100% at 50% 0%, #1a1a3e 0%, #0d0d1a 50%, #050507 100%);
  flex-direction: column;
  padding: 2rem;
  padding-top: 120px;
  padding-bottom: 90px;
  min-height: min(760px, 100svh);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(139, 92, 246, 0.25) 0%, transparent 40%),
      radial-gradient(circle at 80% 70%, rgba(59, 130, 246, 0.2) 0%, transparent 40%),
      radial-gradient(circle at 50% 50%, rgba(236, 72, 153, 0.1) 0%, transparent 60%);
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    padding-top: 80px;
    padding-bottom: 4rem;
    min-height: auto;
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    padding-top: 70px;
    padding-bottom: 3rem;
  }
`;

const HeroContent = styled(motion.div)`
  text-align: center;
  z-index: 10;
  max-width: 960px;
  width: 100%;
  padding: 0 1rem;
`;

const LogoText = styled(motion.h1)`
  font-size: clamp(2.8rem, 7vw, 6rem);
  font-weight: 900;
  background: linear-gradient(135deg, #fff 0%, #c084fc 40%, #8b5cf6 60%, #6366f1 100%);
  background-size: 100% 100%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.03em;
  margin: 0;
  line-height: 0.9;
  text-shadow: 0 0 22px rgba(139, 92, 246, 0.25);
  will-change: transform, opacity;
`;

const HeroTagline = styled(motion.p)`
  font-size: clamp(1.15rem, 2.5vw, 1.65rem);
  color: rgba(255, 255, 255, 0.8);
  margin: 1.5rem auto 0.8rem;
  max-width: 720px;
  font-weight: 300;
  letter-spacing: 0.01em;
`;

const TypewriterText = styled.span`
  display: inline-block;
  color: #a855f7;
  font-weight: 600;
  position: relative;
`;

const CTAButtonGroup = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  align-items: center;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    margin-bottom: 2.5rem;
  }
`;

const CTAPrimaryRow = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    gap: 1rem;
    width: 100%;
    flex-direction: column;
    align-items: center;
  }
`;

const CTAButton = styled(motion.button)`
  padding: 1rem 2.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  
  ${props => props.$primary ? css`
    background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
    color: white;
    border: none;
    /* Keep the primary action calm and easy to read. */
    padding: 0.95rem 2rem;
    font-size: 1rem;
    font-weight: 700;
    box-shadow: 0 12px 32px rgba(139, 92, 246, 0.45);

    &:hover {
      transform: translateY(-3px) scale(1.03);
      box-shadow: 0 20px 48px rgba(139, 92, 246, 0.55);
    }
  ` : css`
    background: transparent;
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.3);
    
    &:hover {
      border-color: #a855f7;
      background: rgba(168, 85, 247, 0.1);
      transform: translateY(-3px);
    }
  `}

  @media (max-width: 480px) {
    padding: 0.875rem 2rem;
    font-size: 1rem;
    width: 100%;
    max-width: 300px;
    justify-content: center;
  }
`;

const HeroDescription = styled.p`
  max-width: 580px;
  margin: 0 auto 2rem;
  color: #aeb4cc;
  font-size: 1rem;
  line-height: 1.7;
`;
const HeroHelpLinks = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.4rem 1.5rem;
  margin-top: 0.5rem;
  @media (max-width: 480px) { gap: 0.25rem 1rem; }
`;

const CTADemoLink = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 4px;
  padding: 0.5rem 0.25rem;
  transition: color 0.2s ease;

  &:hover {
    color: #e9d5ff;
  }
`;

const TourHeroLink = styled(motion.button)`
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(196, 181, 253, 0.14);
  color: rgba(245, 235, 255, 0.94);
  font-size: 0.94rem;
  font-weight: 700;
  cursor: pointer;
  padding: 0.7rem 0.9rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.72rem;
  margin: 0.4rem auto 0;
  text-decoration: none;
  transition: color 0.2s ease, transform 0.2s ease, border-color 0.2s ease, background 0.2s ease;
  border-radius: 999px;
  flex-basis: auto;
  min-height: 52px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.14);

  &:hover {
    color: #ffffff;
    transform: translateY(-1px);
    border-color: rgba(196, 181, 253, 0.26);
    background: rgba(255, 255, 255, 0.055);
  }

  .tour-link-icon {
    width: 30px;
    height: 30px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(168, 85, 247, 0.16);
    border: 1px solid rgba(196, 181, 253, 0.18);
    box-shadow: none;
    flex-shrink: 0;

    svg {
      width: 0.88rem;
      height: 0.88rem;
      stroke-width: 2.3;
    }
  }

  .tour-link-copy {
    display: flex;
    align-items: baseline;
    gap: 0.48rem;
    text-align: left;
    flex-wrap: wrap;
  }

  .tour-link-title {
    font-size: 0.95rem;
    line-height: 1.2;
    font-weight: 800;
  }

  .tour-link-kicker {
    font-size: 0.76rem;
    letter-spacing: 0.02em;
    color: rgba(255, 255, 255, 0.58);
    text-transform: none;
  }

  .tour-link-subtitle {
    display: none;
  }

  @media (max-width: 480px) {
    width: 100%;
    max-width: 300px;
    justify-content: center;
    border-radius: 20px;
    margin-top: 0.5rem;

    .tour-link-copy {
      justify-content: center;
      text-align: center;
    }
  }
`;

const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: 3rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.85rem;
  letter-spacing: 0.1em;
  
  svg {
    animation: ${scrollBounce} 2s ease-in-out infinite;
  }

  @media (max-width: 768px) {
    position: static;
    margin-top: 0.5rem;
  }
`;

const FloatingKeyboard = styled(motion.div)`
  position: absolute;
  font-size: 6rem;
  color: rgba(139, 92, 246, 0.15);
  animation: ${float} 8s ease-in-out infinite;
  filter: none;
  
  &:nth-child(1) { top: 15%; left: 5%; animation-delay: 0s; font-size: 5rem; }
  &:nth-child(2) { top: 50%; right: 5%; animation-delay: 2s; font-size: 7rem; }
  &:nth-child(3) { bottom: 15%; left: 15%; animation-delay: 4s; font-size: 4rem; }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

// ============ FEATURES SECTION ============
const FeaturesSection = styled(Section)`
  background: radial-gradient(ellipse 120% 80% at 50% 100%, #1a1a3e 0%, #0d0d1a 40%, #050507 100%);
  padding: 6rem 2rem;
  min-height: auto;
  padding-top: 8rem;
  padding-bottom: 8rem;
`;

const FeaturesContainer = styled.div`
  max-width: 1400px;
  width: 100%;
  padding: 0 1rem;
  
  @media (max-width: 480px) {
    padding: 0 0.5rem;
  }
`;

const JourneyCarousel = styled.div`
  --journey-gap: 1.2rem;
  --journey-card-width: 320px;
  position: relative;
  overflow: hidden;
  mask-image: linear-gradient(
    to right,
    transparent,
    black 10% 90%,
    transparent
  );

  @media (max-width: 768px) {
    --journey-gap: 0.95rem;
    --journey-card-width: 278px;
    mask-image: linear-gradient(
      to right,
      transparent,
      black 4% 96%,
      transparent
    );
  }
`;

const JourneyTrack = styled.div`
  display: flex;
  gap: var(--journey-gap);
  width: max-content;
  padding: 0.5rem 0;
  will-change: transform;
  animation: ${journeyMarquee} 22s linear infinite;
  animation-direction: ${({ $reverse }) => ($reverse ? 'reverse' : 'normal')};

  ${JourneyCarousel}:hover & {
    animation-play-state: paused;
  }
`;

const JourneyStripCard = styled(motion.button)`
  width: var(--journey-card-width);
  min-width: var(--journey-card-width);
  min-height: 220px;
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  background:
    radial-gradient(circle at top right, rgba(139, 92, 246, 0.14), transparent 45%),
    linear-gradient(180deg, rgba(17, 24, 39, 0.94), rgba(10, 14, 24, 0.98));
  color: inherit;
  padding: 1.2rem;
  text-align: left;
  display: grid;
  grid-template-rows: auto auto 1fr auto;
  gap: 0.7rem;
  cursor: pointer;
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.22);
  transition: transform 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(168, 85, 247, 0.34);
  }
`;

const JourneyStripIcon = styled.div`
  width: 52px;
  height: 52px;
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
  background: linear-gradient(135deg, ${({ $color }) => $color || "#8b5cf6"}, ${({ $colorEnd }) => $colorEnd || "rgba(255,255,255,0.2)"});
  color: white;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.18);
  overflow: hidden;
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    inset: 1px;
    border-radius: inherit;
    background: linear-gradient(180deg, rgba(255,255,255,0.18), rgba(255,255,255,0.02));
    opacity: 0.65;
    z-index: 0;
  }

  ${({ $shape }) => {
    switch ($shape) {
      case 'coach':
        return css`
          border-radius: 18px 18px 12px 18px;
        `;
      case 'practice':
        return css`
          border-radius: 18px;
        `;
      case 'test':
        return css`
          border-radius: 16px;
          transform: rotate(45deg);

          &::before {
            border-radius: 14px;
          }
        `;
      case 'study':
        return css`
          border-radius: 12px 18px 18px 12px;

          &::after {
            content: '';
            position: absolute;
            left: 9px;
            top: 9px;
            bottom: 9px;
            width: 3px;
            border-radius: 999px;
            background: rgba(255, 255, 255, 0.34);
            z-index: 1;
          }
        `;
      case 'community':
        return css`
          clip-path: polygon(25% 6%, 75% 6%, 100% 50%, 75% 94%, 25% 94%, 0% 50%);
        `;
      case 'analytics':
        return css`
          border-radius: 14px;
          clip-path: polygon(0% 100%, 0% 24%, 18% 24%, 18% 100%, 38% 100%, 38% 6%, 58% 6%, 58% 100%, 78% 100%, 78% 38%, 100% 38%, 100% 100%);
        `;
      case 'gamified':
        return css`
          border-radius: 18px;
          clip-path: polygon(50% 0%, 63% 34%, 100% 36%, 71% 58%, 81% 100%, 50% 74%, 19% 100%, 29% 58%, 0% 36%, 37% 34%);
        `;
      case 'steno':
        return css`
          border-radius: 20px 12px 20px 12px;
          transform: rotate(-8deg);
        `;
      case 'jobs':
        return css`
          border-radius: 14px 14px 22px 22px;

          &::after {
            content: '';
            position: absolute;
            left: 14px;
            right: 14px;
            top: 8px;
            height: 8px;
            border: 2px solid rgba(255, 255, 255, 0.36);
            border-bottom: 0;
            border-radius: 8px 8px 0 0;
            z-index: 1;
          }
        `;
      case 'blog':
        return css`
          border-radius: 12px 20px 12px 20px;

          &::after {
            content: '';
            position: absolute;
            left: 11px;
            top: 10px;
            width: 9px;
            height: 12px;
            border-radius: 0 0 8px 0;
            background: rgba(255, 255, 255, 0.26);
            z-index: 1;
          }
        `;
      default:
        return css`
          border-radius: 16px;
        `;
    }
  }}

  svg {
    width: 1.22rem;
    height: 1.22rem;
    stroke-width: 2.15;
    position: relative;
    z-index: 2;
    ${({ $shape }) =>
    $shape === 'test'
      ? 'transform: rotate(-45deg);'
      : $shape === 'steno'
        ? 'transform: rotate(8deg);'
        : ''}
  }
`;

const JourneyStripMeta = styled.div`
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: rgba(196, 181, 253, 0.82);
  font-weight: 700;
`;

const JourneyStripTitle = styled.h3`
  margin: 0;
  color: #fff;
  font-size: 1.35rem;
  line-height: 1.2;
  font-weight: 800;
`;

const JourneyStripDescription = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.95rem;
  line-height: 1.65;
`;

const JourneyStripCta = styled.div`
  color: #c4b5fd;
  font-size: 0.88rem;
  font-weight: 700;
`;

const SectionHeader = styled(motion.div)`
  text-align: center;
  margin-bottom: 4rem;
  
  @media (max-width: 768px) {
    margin-bottom: 2.5rem;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 2rem;
  }
`;

const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 1rem;
    flex-direction: column;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 800;
  color: #fff;
  margin: 0;
  
  span {
    background: linear-gradient(135deg, #a855f7, #3b82f6);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.6);
  max-width: 600px;
  margin: 0 auto;
  padding: 0 1rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0 0.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    padding: 0;
  }
`;

// ============ COURSES SECTION ============
// const orbitAnimation = keyframes` // Unused
//   0% { transform: rotate(0deg) translateX(120px) rotate(0deg); }
//   100% { transform: rotate(360deg) translateX(120px) rotate(-360deg); }
// `;

const CoursesSection = styled(Section)`
  background: radial-gradient(ellipse 120% 80% at 50% 100%, #1a1a3e 0%, #0d0d1a 40%, #050507 100%);
  padding: 8rem 2rem;
  min-height: auto;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: 
      radial-gradient(circle at 20% 50%, rgba(139, 92, 246, 0.15) 0%, transparent 40%),
      radial-gradient(circle at 80% 50%, rgba(59, 130, 246, 0.1) 0%, transparent 40%);
    pointer-events: none;
  }
`;

const CoursesWrapper = styled.div`
  width: 100%;
  max-width: 1400px;
  margin-top: 2rem;
  position: relative;
  z-index: 10;
`;

const CoursesLoading = styled.div`
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  padding: 3rem;
  font-size: 1.1rem;
`;

// Expanding Search Box
const SearchBarContainer = styled(motion.form)`
  font-size: 16px;
  border: solid 0.2em rgba(139, 92, 246, 0.4);
  display: inline-block;
  position: relative;
  border-radius: 2em;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  transition: border-color 0.3s ease;
  flex-shrink: 0;
  
  &:focus-within {
    border-color: rgba(139, 92, 246, 0.7);
    box-shadow: 0 0 20px rgba(139, 92, 246, 0.2);
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const SearchInput = styled.input.attrs({
  type: "text"
})`
  font-family: inherit;
  font-weight: 600;
  width: 2em;
  height: 2em;
  padding: 0.25em 1.8em 0.25em 0.35em;
  border: none;
  box-sizing: border-box;
  border-radius: 2em;
  background: transparent;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1em;
  transition: width 800ms cubic-bezier(0.68, -0.55, 0.27, 1.55) 150ms;
  
  &::placeholder {
    color: transparent;
  }
  
  &:focus {
    outline: none;
    
    &::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }
  }
  
  &:focus, &:not(:placeholder-shown) {
    width: 14em;
    transition: width 800ms cubic-bezier(0.68, -0.55, 0.27, 1.55);
    
    + button[type="reset"] {
      top: 50%;
      right: 0.4em;
      transform: rotate(0deg) translateY(-50%);
      transition: top 150ms ease-out 800ms, right 150ms ease-out 800ms, transform 150ms ease-out 800ms;
    }
    
    + button[type="reset"]:after {
      opacity: 1;
      transition: opacity 150ms ease-out 950ms;
    }
  }
  
  ${props => props.$hasValue && `
    width: 14em;
    transition: width 800ms cubic-bezier(0.68, -0.55, 0.27, 1.55);
    
    + button[type="reset"] {
      top: 50%;
      right: 0.4em;
      transform: rotate(0deg) translateY(-50%);
      transition: top 150ms ease-out 800ms, right 150ms ease-out 800ms, transform 150ms ease-out 800ms;
    }
    
    + button[type="reset"]:after {
      opacity: 1;
      transition: opacity 150ms ease-out 950ms;
    }
  `}
  
  @media (max-width: 768px) {
    &:focus, &:not(:placeholder-shown) {
      width: 12em;
    }
    
    ${props => props.$hasValue && `width: 12em;`}
  }
  
  @media (max-width: 480px) {
    &:focus, &:not(:placeholder-shown) {
      width: 10em;
    }
    
    ${props => props.$hasValue && `width: 10em;`}
  }
`;

const SearchClearButton = styled.button.attrs({
  type: "reset",
  "aria-label": "Clear search input"
})`
  background-color: transparent;
  width: 1.2em;
  height: 1.2em;
  border: 0;
  padding: 0;
  outline: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: calc(160% - 0.5em);
  right: -1.2em;
  transform: rotate(-45deg);
  transform-origin: top left;
  transition: top 150ms ease-out 150ms, right 150ms ease-out 150ms, transform 150ms ease-out 150ms;
  cursor: pointer;
  
  &:before, &:after {
    content: "";
    background-color: rgba(255, 255, 255, 0.7);
    width: 0.2em;
    height: 1.2em;
    position: absolute;
  }
  
  &:after {
    transform: rotate(90deg);
    opacity: 0;
    transition: opacity 150ms ease-out;
  }
  
  &:hover {
    opacity: 0.8;
    
    &:before, &:after {
      background-color: rgba(255, 255, 255, 0.9);
    }
  }
`;

// Horizontal Scrolling Category Pills
const CategoryScrollContainer = styled.div`
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  padding: 1rem 0 2rem;
  margin-bottom: 3rem;
  scrollbar-width: none;
  -ms-overflow-style: none;
  justify-content: center;
  flex-wrap: wrap;
  
  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
  justify-content: flex-start;
    flex-wrap: nowrap;
    padding-left: 1rem;
    padding-right: 1rem;
  }
`;

const CategoryPill = styled(motion.button)`
  padding: 0.75rem 1.5rem;
  border-radius: 100px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  border: 1px solid ${props => props.$active ? 'rgba(168, 85, 247, 0.8)' : 'rgba(255, 255, 255, 0.15)'};
  background: ${props => props.$active
    ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.5) 0%, rgba(59, 130, 246, 0.4) 100%)'
    : 'rgba(255, 255, 255, 0.05)'};
  color: ${props => props.$active ? '#fff' : 'rgba(255, 255, 255, 0.85)'};
  backdrop-filter: blur(20px);
  white-space: nowrap;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;

  ${props => props.$active && `
    box-shadow: 
      0 0 30px rgba(139, 92, 246, 0.3),
      inset 0 0 20px rgba(139, 92, 246, 0.1);
  `}

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    border-color: rgba(168, 85, 247, 0.6);
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(139, 92, 246, 0.2);
    
    &::before {
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    padding: 0.6rem 1rem;
    font-size: 0.75rem;
    color: ${props => props.$active ? '#fff' : 'rgba(255, 255, 255, 0.9)'};
  }
`;

// 3D Card Grid with perspective
// Canvas-style card animations
const drawLine = keyframes`
  from {
    stroke-dashoffset: 2000;
  }
  to {
    stroke-dashoffset: 0;
  }
`;

const eraseLine = keyframes`
  from {
    stroke-dashoffset: 0;
  }
  to {
    stroke-dashoffset: 2000;
  }
`;

const CourseCardGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 3rem;
  width: 100%;
  padding: 3rem 1rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 900px) {
    gap: 2.5rem;
    padding: 2rem 1rem;
  }
  
  @media (max-width: 550px) {
    gap: 2rem;
    padding: 1.5rem 0.5rem;
  }
`;

// Canvas-style card with animated border
const CourseItem = styled(motion.a)`
  position: relative;
  display: block;
  width: 220px;
  height: 220px;
  padding: 16px;
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  background: ${props => props.$bgColor || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  border-radius: 8px;
  
  @media (max-width: 900px) {
    width: 200px;
    height: 200px;
    padding: 14px;
  }
  
  @media (max-width: 550px) {
    width: 130px;
    height: 130px;
    padding: 10px;
  }
`;

const CourseCardHover = styled.div`
  position: absolute;
  inset: 0;
  z-index: 120;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 10, 30, 0.72);
  border-radius: 8px;
  opacity: 0;
  transition: opacity 0.25s ease;
  pointer-events: none;

  span {
    padding: 0.55rem 1.1rem;
    border-radius: 999px;
    background: linear-gradient(135deg, #a855f7, #6366f1);
    color: #fff;
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  }

  ${CourseItem}:hover &,
  ${CourseItem}:focus-visible & {
    opacity: 1;
  }
`;

// Animated SVG border
const CanvasBorder = styled.div`
  position: absolute;
  top: 40px;
  left: -40px;
  height: 100%;
  width: 100%;
  z-index: 0;
  transition: all 0.25s ease-in-out;
  transform: rotate(-10deg) skew(-10deg);

  svg {
    height: 100%;
    width: 100%;
  }
  
  @media (max-width: 900px) {
    top: 30px;
    left: -30px;
  }
  
  @media (max-width: 550px) {
    top: 20px;
    left: -20px;
  }
`;

const RectGradient = styled.rect`
  stroke-dasharray: 2000;
  stroke-dashoffset: 2000;
  animation: ${eraseLine} 1s ease-in-out forwards;
  fill: none;
  stroke-linecap: square;
  stroke-width: 4;
  stroke-miterlimit: 30;
  width: 100%;
  height: 100%;
  
  ${CourseItem}:hover & {
    animation: ${drawLine} 3s cubic-bezier(0.19, 1, 0.22, 1) forwards;
  }
`;

// Image wrapper with rotation
const CanvasImgWrapper = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  transform: rotate(-10deg) skew(-10deg);
  overflow: hidden;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px);
  border-radius: 8px;
  transition: all 0.25s ease-in-out;
  
  ${CourseItem}:hover & {
    transform: rotate(-14deg) skew(-14deg) scale(0.96);
    background: rgba(255, 255, 255, 0.2);
  }
`;

const CanvasImg = styled.img`
  transform: scale3d(0.9, 0.9, 0.9);
  opacity: 0.4;
  max-width: 170px;
  max-height: 170px;
  width: 170px;
  height: 170px;
  transition: all 0.25s ease-in-out;
  object-fit: cover;
  filter: brightness(1.2);
  border-radius: 8px;
  
  ${CourseItem}:hover & {
    opacity: 0.9;
    transform: scale3d(1, 1, 1);
  }
  
  @media (max-width: 900px) {
    max-width: 150px;
    max-height: 150px;
    width: 150px;
    height: 150px;
  }
  
  @media (max-width: 550px) {
    max-width: 100px;
    max-height: 100px;
    width: 100px;
    height: 100px;
  }
`;

// Text content positioned on the side
const CanvasCopy = styled.div`
  position: absolute;
  bottom: 0;
  left: ${props => props.isLeft ? '-25%' : '85%'};
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.95);
  z-index: 100;
  
  @media (max-width: 550px) {
    left: ${props => props.isLeft ? '-15%' : '75%'};
  }
`;

const CanvasCopySubtitle = styled.span`
  display: block;
  transform: rotate(270deg) translateY(-100%) translateX(calc(-100% - 80px));
  transform-origin: top left;
  position: absolute;
  left: 0;
  top: 6px;
  font-size: 16px;
  font-weight: 900;
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  transition: all 0.75s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.35s;
  opacity: 0;
  
  ${CourseItem}:hover & {
    opacity: 1;
    transform: rotate(270deg) translateY(-100%) translateX(-100%);
  }
  
  @media (max-width: 900px) {
    font-size: 13px;
    top: 5px;
    transform: rotate(270deg) translateY(-100%) translateX(calc(-100% - 60px));
    
    ${CourseItem}:hover & {
      transform: rotate(270deg) translateY(-100%) translateX(-100%);
    }
  }
  
  @media (max-width: 550px) {
    font-size: 8px;
    top: 3px;
    transform: rotate(270deg) translateY(-100%) translateX(calc(-100% - 40px));
    
    ${CourseItem}:hover & {
      transform: rotate(270deg) translateY(-100%) translateX(-100%);
    }
  }
`;

const CanvasCopyTitle = styled.strong`
  font-size: 38px;
  display: block;
  transform: translateX(-80px);
  transition: all 0.75s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  color: rgba(255, 255, 255, 0.95);
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.4);
  opacity: 0;
  font-weight: 900;
  line-height: 1.1;
  
  &:nth-child(2) {
    transition-delay: 0.1s;
  }
  
  &:nth-child(3) {
    transition-delay: 0.2s;
  }
  
  ${CourseItem}:hover & {
    opacity: 1;
    transform: translateX(0);
  }
  
  @media (max-width: 900px) {
    font-size: 28px;
    transform: translateX(-60px);
    
    ${CourseItem}:hover & {
      transform: translateX(0);
    }
  }
  
  @media (max-width: 550px) {
    font-size: 14px;
    transform: translateX(-30px);
    
    ${CourseItem}:hover & {
      transform: translateX(0);
    }
  }
`;

const CanvasCopyDetails = styled.span`
  display: block;
  transition: all 0.75s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.14s;
  transform: translateX(-80px);
  opacity: 0;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.3);
  font-weight: 600;
  margin-top: 4px;
  
  ${CourseItem}:hover & {
    opacity: 1;
    transform: translateX(0);
  }
  
  @media (max-width: 900px) {
    font-size: 10px;
    transform: translateX(-60px);
    
    ${CourseItem}:hover & {
      transform: translateX(0);
    }
  }
  
  @media (max-width: 550px) {
    font-size: 7px;
    transform: translateX(-30px);
    margin-top: 2px;
    
    ${CourseItem}:hover & {
      transform: translateX(0);
    }
  }
`;

// const CourseItemHeader = styled.div` // Unused
//   display: flex;
//   align-items: flex-start;
//   gap: 1rem;
//   margin-bottom: 1.25rem;
// `;

// const CourseIconWrapper = styled.div` // Unused
//   width: 64px;
//   height: 64px;
//   border-radius: 16px;
//   background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   font-size: 1.75rem;
//   flex-shrink: 0;
//   border: 1px solid rgba(255, 255, 255, 0.1);
//   overflow: hidden;
//   padding: 8px;
//   
//   img {
//     width: 100%;
//     height: 100%;
//     object-fit: contain;
//   }
// `;

// const CourseItemInfo = styled.div` // Unused
//   flex: 1;
//   min-width: 0;
// `;

// const CourseItemTitle = styled.h3` // Unused
//   font-size: 1.1rem;
//   font-weight: 700;
//   color: #fff;
//   margin: 0;
//   line-height: 1.4;
//   text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
//   display: -webkit-box;
//   -webkit-line-clamp: 2;
//   -webkit-box-orient: vertical;
//   overflow: hidden;
// `;

// const CourseItemMeta = styled.div` // Unused
//   display: flex;
//   align-items: center;
//   gap: 0.5rem;
//   font-size: 0.75rem;
//   color: rgba(255, 255, 255, 0.7);
// `;

// const MetaBadge = styled.span` // Unused
//   padding: 0.2rem 0.6rem;
//   background: rgba(255, 255, 255, 0.15);
//   backdrop-filter: blur(10px);
//   border-radius: 50px;
//   color: rgba(255, 255, 255, 0.9);
//   font-weight: 500;
//   font-size: 0.7rem;
//   text-transform: uppercase;
//   letter-spacing: 0.5px;
// `;

// const CourseItemDescription = styled.p` // Unused
//   font-size: 0.9rem;
//   color: rgba(255, 255, 255, 0.6);
//   line-height: 1.6;
//   margin-bottom: 1.25rem;
//   display: -webkit-box;
//   -webkit-line-clamp: 2;
//   -webkit-box-orient: vertical;
//   overflow: hidden;
// `;

// const CourseItemFooter = styled.div` // Unused
//   display: flex;
//   align-items: center;
//   justify-content: flex-end;
//   padding-top: 1rem;
//   border-top: 1px solid rgba(255, 255, 255, 0.06);
// `;

// const CourseItemPrice = styled.div` // Unused
//   display: flex;
//   align-items: baseline;
//   gap: 0.5rem;
// `;

// const PriceAmount = styled.span` // Unused
//   font-size: 1.5rem;
//   font-weight: 800;
//   background: linear-gradient(135deg, #a855f7, #6366f1);
//   -webkit-background-clip: text;
//   -webkit-text-fill-color: transparent;
//   background-clip: text;
// `;

// const PriceLabel = styled.span` // Unused
//   font-size: 0.8rem;
//   color: rgba(255, 255, 255, 0.4);
// `;

// const ExploreButton = styled.div` // Unused
//   display: flex;
//   align-items: center;
//   gap: 0.5rem;
//   color: #a855f7;
//   font-size: 0.9rem;
//   font-weight: 600;
//   transition: all 0.3s ease;

//   svg {
//     transition: transform 0.3s ease;
//   }

//   ${CourseItem}:hover & {
//     color: #c084fc;
//     
//     svg {
//       transform: translateX(4px);
//     }
//   }
// `;

const ViewAllButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin: 3rem auto 0;
  padding: 1rem 2.5rem;
  background: transparent;
  border: 2px solid rgba(168, 85, 247, 0.4);
  border-radius: 100px;
  color: #a855f7;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  z-index: 10;
  min-height: 44px;
  touch-action: manipulation;

  &:hover {
    background: rgba(168, 85, 247, 0.1);
    border-color: rgba(168, 85, 247, 0.8);
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(139, 92, 246, 0.2);
  }

  &:active {
    transform: translateY(-1px);
  }
`;

// ============ STATS SECTION ============
const StatsSection = styled(Section)`
  background: #050507;
  padding: 8rem 2rem;
  min-height: auto;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.08) 0%, transparent 60%);
    pointer-events: none;
  }
`;

const StatsContainer = styled.div`
  display: flex;
    flex-wrap: wrap;
    justify-content: center;
  gap: 4rem;
  max-width: 1200px;
`;

const StatItem = styled(motion.div)`
  text-align: center;
  min-width: 200px;
`;

const StatNumber = styled.div`
  font-size: clamp(3.5rem, 10vw, 6rem);
  font-weight: 900;
  background: linear-gradient(135deg, #c084fc 0%, #a855f7 50%, #ec4899 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1;
  filter: drop-shadow(0 0 30px rgba(168, 85, 247, 0.3));
`;

const StatLabel = styled.div`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  font-weight: 500;
`;

// ============ TESTIMONIALS SECTION ============
const TestimonialsSection = styled(Section)`
  background: radial-gradient(ellipse 100% 100% at 50% 0%, #12121f 0%, #050507 100%);
  padding: 8rem 2rem;
  min-height: auto;
  
  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 3rem 0.5rem;
  }
`;

const TestimonialCard = styled(motion.div)`
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  padding: 3rem;
  max-width: 700px;
  width: 100%;
  text-align: center;
  position: relative;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    border-radius: 16px;
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
    border-radius: 12px;
  }
`;

const QuoteIcon = styled.div`
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #a855f7, #6366f1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.25rem;
  
  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    font-size: 1rem;
    top: -15px;
  }
`;

const TestimonialText = styled.p`
  font-size: 1.3rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.8;
  margin-bottom: 2rem;
  font-style: italic;
  word-wrap: break-word;
  overflow-wrap: break-word;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.95rem;
    line-height: 1.5;
    margin-bottom: 1rem;
  }
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    gap: 0.75rem;
    flex-direction: column;
    text-align: center;
  }
`;

const AuthorAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #a855f7, #3b82f6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 1.2rem;
  flex-shrink: 0;
  
  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }
`;

const AuthorInfo = styled.div`
  text-align: left;
  min-width: 0;
  flex: 1;
  
  @media (max-width: 480px) {
    text-align: center;
  }
  
  .name {
    color: #fff;
    font-weight: 600;
    font-size: 1.1rem;
    word-break: break-word;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
    
    @media (max-width: 480px) {
      font-size: 0.9rem;
    }
  }
  
  .role {
    color: rgba(255, 255, 255, 0.5);
    font-size: 0.9rem;
    word-break: break-word;
    
    @media (max-width: 480px) {
      font-size: 0.8rem;
    }
  }
`;

const StarRating = styled.div`
  display: flex;
  gap: 0.25rem;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: #fbbf24;
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    gap: 0.15rem;
    margin-bottom: 1rem;
    font-size: 0.9rem;
  }
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  margin-top: 3rem;
  width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-top: 2rem;
    padding: 0 0.5rem;
  }
  
  @media (max-width: 480px) {
    gap: 1rem;
    margin-top: 1.5rem;
    padding: 0;
  }
`;

// ============ FAQ SECTION ============
const FAQSection = styled(Section)`
  background: linear-gradient(180deg, #050507 0%, #0a0a14 100%);
  padding: 8rem 2rem;
  min-height: auto;
`;

const ExamGuidesSection = styled(Section)`
  background: #0a0a14;
  padding: 6rem 2rem;
  min-height: auto;
  flex-direction: column;
`;

const ExamGuidesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
  max-width: 1100px;
  margin: 2rem auto 0;
  width: 100%;
`;

const ExamGuideLink = styled.a`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 0.28rem;
  min-height: 84px;
  padding: 0.9rem 1rem;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(139, 92, 246, 0.22);
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  line-height: 1.38;
  transition: border-color 0.2s, color 0.2s, background 0.2s;

  &:hover {
    border-color: rgba(0, 255, 136, 0.45);
    color: #00ff88;
    background: rgba(255, 255, 255, 0.05);
  }
`;

const ExamGuideTitle = styled.span`
  color: #f8fafc;
  font-size: 0.95rem;
  font-weight: 600;
`;

const ExamGuideMeta = styled.span`
  color: rgba(255, 255, 255, 0.56);
  font-size: 0.78rem;
`;

const ExamGuidesActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.75rem 1rem;
  margin-top: 1.35rem;
`;

const ExamGuidesToggleButton = styled.button`
  padding: 0.65rem 1.15rem;
  border-radius: 999px;
  border: 1px solid rgba(139, 92, 246, 0.35);
  background: rgba(139, 92, 246, 0.08);
  color: #c4b5fd;
  font-size: 0.86rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(0, 255, 136, 0.45);
    color: #86efac;
    background: rgba(16, 185, 129, 0.08);
  }
`;

const ExamGuidesHubLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
  padding: 0.62rem 1rem;
  border-radius: 999px;
  border: 1px solid rgba(139, 92, 246, 0.28);
  background: rgba(139, 92, 246, 0.06);
  color: #c4b5fd;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.86rem;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(0, 255, 136, 0.45);
    color: #86efac;
    background: rgba(16, 185, 129, 0.08);
  }
`;

const FAQContainer = styled(FeaturesContainer)`
  max-width: 900px;
`;

const FAQItem = styled(motion.div)`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(139, 92, 246, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(139, 92, 246, 0.4);
  }
`;

const FAQQuestion = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 1.15rem;
  font-weight: 700;
  color: #f8fafc;
  margin-bottom: ${props => props.isOpen ? '1rem' : '0'};
  
  span {
    color: #c4b5fd;
    font-size: 1.5rem;
    transition: transform 0.3s ease;
    transform: ${props => props.isOpen ? 'rotate(45deg)' : 'rotate(0)'};
  }
`;

const FAQAnswer = styled(motion.div)`
  color: rgba(255, 255, 255, 0.88);
  line-height: 1.8;
  font-size: 1.02rem;
  
  a {
    color: #a855f7;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

// ============ CTA SECTION ============
const CTASection = styled(Section)`
  background: #050507;
  padding: 10rem 2rem;
  min-height: 80vh;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: 
      radial-gradient(ellipse 80% 60% at 50% 50%, rgba(139, 92, 246, 0.15) 0%, transparent 70%),
      radial-gradient(circle at 20% 80%, rgba(236, 72, 153, 0.1) 0%, transparent 40%),
      radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.1) 0%, transparent 40%);
    pointer-events: none;
  }
`;

const CTAContent = styled(motion.div)`
  text-align: center;
  z-index: 10;
  max-width: 800px;
`;

const CTATitle = styled.h2`
  font-size: clamp(2rem, 6vw, 4rem);
  font-weight: 900;
  color: #fff;
  margin-bottom: 1.5rem;
  line-height: 1.2;
`;

const CTASubtitle = styled.p`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 2.5rem;
`;

const InstituteFooterBanner = styled.div`
  background: linear-gradient(135deg, rgba(30, 27, 75, 0.4) 0%, rgba(49, 46, 129, 0.4) 100%);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 20px;
  padding: 2.5rem 2rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  max-width: 1200px;
  margin: 0 auto 4rem auto;
  
  &::before {
    content: '🏫';
    position: absolute;
    font-size: 8rem;
    opacity: 0.03;
    right: 5%;
    top: 50%;
    transform: translateY(-50%);
  }
`;

const InstituteBannerTitle = styled.h3`
  font-size: 1.75rem;
  font-weight: 800;
  color: #fff;
  margin-bottom: 0.75rem;
  
  span {
    background: linear-gradient(135deg, #a855f7 0%, #3b82f6 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const InstituteBannerDesc = styled.p`
  color: #cbd5e1;
  font-size: 1rem;
  max-width: 600px;
  margin: 0 auto 1.5rem;
  line-height: 1.5;
`;

const InstituteBannerCTA = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.875rem 1.5rem;
  background: #4f46e5;
  color: white;
  font-weight: 600;
  font-size: 1rem;
  border-radius: 12px;
  text-decoration: none;
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px -5px rgba(79, 70, 229, 0.4);
    color: white;
  }
`;

// ============ FOOTER ============
const Footer = styled.footer`
  background: linear-gradient(180deg, #050507 0%, #030304 100%);
  padding: 5rem 2rem 2rem;
  border-top: 1px solid rgba(139, 92, 246, 0.1);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 200px;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.5), transparent);
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 2rem 1.5rem;
  align-items: start;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1.75rem 1.25rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.5rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FooterColumn = styled.div``;

const FooterTitle = styled.h4`
  color: #fff;
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
`;

const FooterText = styled.p`
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.7;
  margin-bottom: 1rem;
`;

const FooterLink = styled.a`
  display: block;
  color: rgba(255, 255, 255, 0.5);
  text-decoration: none;
  margin-bottom: 0.55rem;
  font-size: 0.92rem;
  line-height: 1.35;
  transition: color 0.3s ease;

  &:hover {
    color: #a855f7;
  }
`;

const FooterSectionNote = styled.p`
  color: rgba(255, 255, 255, 0.52);
  font-size: 0.84rem;
  line-height: 1.45;
  margin: 0 0 0.8rem;
`;


const FooterSocialStrip = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1.5rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

const FooterSocialTop = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 0.15rem 0.25rem;
  font-size: 1.5rem;
`;

const FooterBrandIcon = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  color: rgba(255, 255, 255, 0.72);
  transition: color 350ms ease-in-out, transform 350ms ease-in-out;
  text-decoration: none;

  &:hover {
    color: ${(p) => p.$hoverColor};
    transform: scale(1.4);
  }

  svg {
    display: block;
  }
`;

const footerHeartBeat = keyframes`
  to {
    transform: scale(1.35);
  }
`;

const FooterBottom = styled.div`
  text-align: center;
  padding-top: 2rem;
  margin-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.9rem;

  .heart-beat {
    display: inline-block;
    color: #f87171;
    animation: ${footerHeartBeat} 0.45s ease infinite alternate;
  }
`;

const NewsletterSection = styled.div`
  margin-top: 1rem;
`;

const NewsletterInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(168, 85, 247, 0.3);
  border-radius: 8px;
  color: #fff;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  
  &:focus {
    outline: none;
    border-color: #a855f7;
    box-shadow: 0 0 0 2px rgba(168, 85, 247, 0.2);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const NewsletterButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  background: linear-gradient(135deg, #a855f7 0%, #6366f1 100%);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(168, 85, 247, 0.4);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const NewsletterMessage = styled.div`
  margin-top: 0.5rem;
  font-size: 0.85rem;
  color: ${props => props.success ? '#00ff88' : '#ff6b6b'};
`;

// ============ DATA ============
const features = [
  {
    icon: <FiZap />,
    shape: "coach",
    benefit: "Personalized feedback",
    title: "Typogram AI Coach",
    description: "AI-powered typing coach analyzes your keystroke patterns, identifies weak keys, and generates personalized practice text. Get real-time insights and recommendations to improve faster.",
    color: "#8b5cf6",
    colorEnd: "#a855f7",
    link: "/profile",
  },
  {
    icon: <FiTarget />,
    shape: "practice",
    benefit: "SSC · RRB · Banking",
    title: "Exam-Ready Practice",
    description: "Prepare for SSC, CHSL, RRB, and Banking exams with real test patterns and timed practice sessions.",
    color: "#3b82f6",
    colorEnd: "#6366f1",
  },
  {
    icon: <FiTrendingUp />,
    shape: "analytics",
    benefit: "WPM & accuracy trends",
    title: "Real-Time Analytics",
    description: "Track your WPM, accuracy, and progress with beautiful charts and actionable insights to improve faster.",
    color: "#ec4899",
    colorEnd: "#f472b6",
  },
  {
    icon: <FiAward />,
    shape: "gamified",
    benefit: "XP · races · leaderboards",
    title: "Gamified Experience",
    description: "Earn XP, unlock achievements, compete in tournaments, and climb the leaderboards while mastering typing.",
    color: "#10b981",
    colorEnd: "#34d399",
  },
];

const stats = [
  { number: "50K+", label: "Active Learners" },
  { number: "95%", label: "Success Rate" },
  { number: "10M+", label: "Words Typed" },
  { number: "4.9★", label: "User Rating" }
];

const guidedJourneys = [
  {
    icon: <FiTarget />,
    shape: 'practice',
    benefit: "Daily speed work",
    title: "Open direct practice",
    description: "Jump into instant typing practice, try themes, trainer mode, and build consistency without choosing a course first.",
    color: "#a855f7",
    colorEnd: "#7c3aed",
    link: "/typing-display",
  },
  {
    icon: <FiPlayCircle />,
    shape: 'test',
    benefit: "Fastest path",
    title: "Start a typing test",
    description: "Jump straight into the guided exam funnel, pick your course, and begin a real test flow in minutes.",
    color: "#8b5cf6",
    colorEnd: "#6366f1",
    link: "/start-test",
  },
  {
    icon: <FiBookOpen />,
    shape: 'study',
    benefit: "Mocks · results · mistake practice",
    title: "Mock Tests & Exam Prep",
    description: "Choose an exam, complete a timed mock, review your results and reattempt the questions you missed.",
    color: "#10b981",
    colorEnd: "#22c55e",
    link: EXAM_PREP_PATH,
    featureKey: "study_schedule",
  },
  {
    icon: <FiUsers />,
    shape: 'community',
    benefit: "Stay connected",
    title: "Explore community",
    description: "Open the community hub to discover spaces, discussions, and study-driven social features inside Typogram.",
    color: "#06b6d4",
    colorEnd: "#3b82f6",
    link: "/community",
  },
  {
    icon: <FiEdit3 />,
    shape: 'steno',
    benefit: "Skill expansion",
    title: "Practice steno",
    description: "Switch into steno mode for shorthand-focused practice and build a separate exam-ready routine.",
    color: "#ec4899",
    colorEnd: "#f43f5e",
    link: "/steno/practice",
  },
  {
    icon: <FiBriefcase />,
    shape: 'jobs',
    benefit: "Exam discovery",
    title: "Track job updates",
    description: "Browse new government notices, open official links, and move from job discovery into typing preparation faster.",
    color: "#f97316",
    colorEnd: "#ef4444",
    link: "/job-updates",
  },
  {
    icon: <FiFileText />,
    shape: 'blog',
    benefit: "Learn faster",
    title: "Read blog guides",
    description: "Browse typing tips, exam explainers, and strategy posts when you want help before starting practice.",
    color: "#f59e0b",
    colorEnd: "#f97316",
    link: "/blog",
  },
];

const typingWords = ["Speed & Exam", "Accuracy & Exam", "Success & Exam", "Skills & Exam", "Future & Exam"];

// Category icons mapping
const categoryIcons = {
  'Central Government Exams': '🏛️',
  'Police Recruitment Exams': '🚔',
  'Central Armed Police Forces Exams': '🎖️',
  'Railway Recruitment Exams': '🚂',
  'Medical Institution Exams': '🏥',
  'Banking Exams': '🏦',
};

// React Icon components for cards
// const getCategoryIcon = (category) => { // Unused
//   const icons = {
//     'Central Government Exams': <FaGraduationCap />,
//     'Police Recruitment Exams': <FaShieldAlt />,
//     'Central Armed Police Forces Exams': <FaStar />,
//     'Railway Recruitment Exams': <FaTrain />,
//     'Medical Institution Exams': <FaHeart />,
//     'Banking Exams': <FaUniversity />,
//   };
//   return icons[category] || <FaGraduationCap />;
// };

// ============ QUICK-VIEW MODAL STYLES ============
const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const ModalPanel = styled(motion.div)`
  width: 100%;
  max-width: 900px;
  height: 100vh;
  background: linear-gradient(135deg, #0d0d1a 0%, #1a1a3e 50%, #0d0d1a 100%);
  overflow-y: auto;
  position: relative;
  box-shadow: -20px 0 60px rgba(0, 0, 0, 0.5);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 20%, rgba(139, 92, 246, 0.15) 0%, transparent 40%),
      radial-gradient(circle at 80% 80%, rgba(59, 130, 246, 0.1) 0%, transparent 40%);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const ModalCloseButton = styled(motion.button)`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(168, 85, 247, 0.3);
    border-color: rgba(168, 85, 247, 0.5);
    transform: rotate(90deg);
  }
`;

const ModalContent = styled.div`
  padding: 2.5rem;
  position: relative;
  z-index: 5;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
  text-align: center;
  }
`;

const ModalCourseImage = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 20px;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(59, 130, 246, 0.2) 100%);
  border: 1px solid rgba(139, 92, 246, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  padding: 0.75rem;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const ModalCourseInfo = styled.div`
  flex: 1;
`;

const ModalCourseTitle = styled.h2`
  font-size: 2rem;
  font-weight: 800;
  color: #fff;
  margin-bottom: 0.5rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ModalCourseCategory = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(139, 92, 246, 0.2);
  border-radius: 50px;
  color: #a855f7;
  font-size: 0.9rem;
  font-weight: 600;
`;

const ModalCourseDesc = styled.p`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.7;
  margin-top: 1rem;
`;

const BenefitsSection = styled.div`
  margin-bottom: 2rem;
`;

const BenefitsTitle = styled.h3`
    font-size: 1.25rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const BenefitsList = styled.div`
    display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;

  .icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(34, 197, 94, 0.1) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #22c55e;
    font-size: 1.1rem;
  }

  .text {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.8);
    font-weight: 500;
  }
`;

const TypingCoursesSection = styled.div`
  margin-top: 2rem;
`;

const TypingCoursesSectionTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 1.5rem;

  span {
    background: linear-gradient(135deg, #a855f7, #3b82f6);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const TypingCoursesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const TypingCourseItem = styled(motion.div)`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
    transition: all 0.3s ease;

    &:hover {
    border-color: rgba(139, 92, 246, 0.4);
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%);
  }

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
  }
`;

const TypingCourseItemInfo = styled.div`
  flex: 1;
`;

const TypingCourseItemTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.25rem;
`;

const TypingCourseItemMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.5);

  @media (max-width: 600px) {
    justify-content: center;
  }
`;

const TypingCourseRulesLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.45rem;
  padding: 0;
  background: none;
  border: none;
  color: #93c5fd;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
  &:hover {
    color: #bfdbfe;
  }
`;

const TypingCourseItemPrice = styled.div`
  font-size: 1.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  white-space: nowrap;
`;

const TypingCourseItemButton = styled(motion.button)`
  padding: 0.75rem 1.5rem;
  background: ${props => props.disabled
    ? 'rgba(255, 255, 255, 0.1)'
    : props.isFree
      ? 'linear-gradient(135deg, #22c55e, #16a34a)'
      : 'linear-gradient(135deg, #8b5cf6, #6366f1)'};
  color: ${props => props.disabled ? 'rgba(255, 255, 255, 0.4)' : '#fff'};
  border: none;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  white-space: nowrap;
    transition: all 0.3s ease;

  &:hover:not(:disabled) {
      transform: translateY(-2px);
    box-shadow: 0 8px 25px ${props => props.isFree ? 'rgba(34, 197, 94, 0.4)' : 'rgba(139, 92, 246, 0.4)'};
  }
`;

const QuickStartCTA = styled(motion.div)`
  margin-top: 2rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(59, 130, 246, 0.15) 100%);
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 20px;
  text-align: center;
`;

const QuickStartText = styled.p`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 1rem;
`;

const QuickStartButton = styled(motion.button)`
  padding: 1rem 2.5rem;
  background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
  color: #fff;
  border: none;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 30px rgba(139, 92, 246, 0.4);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 40px rgba(139, 92, 246, 0.5);
  }
`;

// ============ FAQ ACCORDION COMPONENT ============
const FAQAccordion = ({ faqs }) => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <>
      {faqs.map((faq, index) => {
        const isOpen = openIndex === index;
        return (
          <FAQItem
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            onClick={() => setOpenIndex(isOpen ? null : index)}
          >
            <FAQQuestion isOpen={isOpen}>
              {faq.q}
              <span>+</span>
            </FAQQuestion>
            {isOpen && (
              <FAQAnswer
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                dangerouslySetInnerHTML={{ __html: faq.a }}
              />
            )}
          </FAQItem>
        );
      })}
    </>
  );
};

// ============ MAIN COMPONENT ============
const HomeComponent = () => {
  const navigate = useNavigate();
  const { isFeatureEnabled } = useFeatureFlags();
  const { startJourney, resetJourney } = useGuidedTour();
  const [currentWord, setCurrentWord] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [categories, setCategories] = useState(['All Categories']);
  const [searchQuery, setSearchQuery] = useState('');

  // Modal state
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [typingCourses, setTypingCourses] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [activeCourses, setActiveCourses] = useState([]);
  const [showTelegramModal, setShowTelegramModal] = useState(false);
  const [pendingFreeCourse, setPendingFreeCourse] = useState(null);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [showAllExamGuides, setShowAllExamGuides] = useState(false);
  const [rulesModalTypingCourse, setRulesModalTypingCourse] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [feedbacksLoading, setFeedbacksLoading] = useState(true);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterMessage, setNewsletterMessage] = useState('');

  useEffect(() => {
    const user = AuthService.getCurrentUser();
    setIsLoggedIn(!!user);

    const wordInterval = setInterval(() => {
      setCurrentWord(prev => (prev + 1) % typingWords.length);
    }, 2500);

    return () => clearInterval(wordInterval);
  }, []);

  // Fetch all courses
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await CourseDataService.getAllCourses();
        const coursesData = response.data || [];
        setCourses(coursesData);

        // Extract unique categories from courses
        const uniqueCategories = ['All Categories', ...new Set(
          coursesData
            .map(course => course.category)
            .filter(Boolean)
        )];
        setCategories(uniqueCategories);
      } catch (err) {
        console.log("Error fetching courses:", err);
      } finally {
        setCoursesLoading(false);
      }
    };
    fetchCourses();
  }, []);

  const fetchActiveCourses = async () => {
    try {
      const response = await CourseDataService.getTypingCoursesForAuthenticatedUser();
      setActiveCourses(response.data || []);
    } catch (err) {
      console.log("Error fetching active courses:", err);
    }
  };

  // Fetch approved feedbacks for testimonials
  useEffect(() => {
    const fetchFeedbacks = async () => {
      try {
        const response = await FeedbackService.getApprovedFeedbacks(6);
        setFeedbacks(response.data || []);
      } catch (err) {
        console.log("Error fetching feedbacks:", err);
      } finally {
        setFeedbacksLoading(false);
      }
    };
    fetchFeedbacks();
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      fetchActiveCourses();
    } else {
      setActiveCourses([]);
    }
  }, [isLoggedIn]);

  // Handle newsletter subscription
  const handleNewsletterSubscribe = async (e) => {
    e.preventDefault();
    if (!newsletterEmail || !newsletterEmail.includes('@')) {
      setNewsletterMessage('Please enter a valid email address');
      return;
    }

    setNewsletterLoading(true);
    setNewsletterMessage('');
    try {
      await NewsletterService.subscribe(newsletterEmail);
      setNewsletterMessage('Successfully subscribed to newsletter!');
      setNewsletterEmail('');
    } catch (err) {
      setNewsletterMessage(err.response?.data?.message || 'Failed to subscribe. Please try again.');
    } finally {
      setNewsletterLoading(false);
    }
  };

  // Filter courses by category and search query
  const getFilteredCourses = () => {
    let filtered = courses;

    // Filter by category
    if (selectedCategory !== 'All Categories') {
      filtered = filtered.filter(course => course.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(course =>
        (course.title || '').toLowerCase().includes(query) ||
        (course.description || '').toLowerCase().includes(query) ||
        (course.category || '').toLowerCase().includes(query) ||
        (course.subject || '').toLowerCase().includes(query)
      );
    }

    return filtered;
  };

  const handleGetStarted = () => {
    if (isLoggedIn) {
      navigate('/profile');
    } else {
      navigate('/register');
    }
  };

  const handleTryDemo = () => {
    navigate('/start-test', { state: { isDemo: true } });
  };

  const handleLaunchTour = () => {
    resetJourney("new-user-onboarding");
    startJourney("new-user-onboarding", { force: true, startIndex: 0 });
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
  };

  // Open modal with course details
  const handleCourseClick = async (course) => {
    setSelectedCourse(course);
    setModalLoading(true);

    // Fetch active courses when modal opens (only if user is logged in and not already fetched)
    const user = AuthService.getCurrentUser();
    if (user && activeCourses.length === 0) {
      fetchActiveCourses();
    }

    try {
      const response = await CourseDataService.getCourseById(course.id);
      const courseData = response.data;
      // Update selectedCourse with full data including benefits
      setSelectedCourse({
        ...course,
        ...courseData,
        benefits: courseData.benefits || []
      });
      setTypingCourses(courseData.typingCourses || []);
    } catch (err) {
      console.log("Error fetching course details:", err);
      setTypingCourses([]);
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedCourse(null);
    setTypingCourses([]);
  };

  const handleAddToActiveCourses = async (typingCourse) => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }

    if (typingCourse.price > 0) {
      handleCloseModal();
      navigate("/checkout", { state: { course: typingCourse } });
      return;
    }

    // Free course: show Telegram join modal first, then enroll on continue
    setPendingFreeCourse(typingCourse);
    setShowTelegramModal(true);
  };

  const enrollPendingFreeCourse = async () => {
    if (!pendingFreeCourse) return;
    const typingCourse = pendingFreeCourse;
    setShowTelegramModal(false);
    setPendingFreeCourse(null);
    try {
      const enrolledAt = new Date().toISOString().split("Z")[0];
      const response = await CourseDataService.addTypingCourseToAuthenticatedUser(
        typingCourse.id,
        enrolledAt
      );

      if (response.status === 200 || response.status === 201 || response.status === 409) {
        setActiveCourses(prev => {
          if (prev.some(ac => ac.id === typingCourse.id)) return prev;
          return [...prev, typingCourse];
        });
      }
    } catch (error) {
      if (error.response?.status === 409) {
        setActiveCourses(prev => {
          if (prev.some(ac => ac.id === typingCourse.id)) return prev;
          return [...prev, typingCourse];
        });
      } else if (error.response?.status === 403 && error.response?.data?.freeLimitReached) {
        const limit = error.response.data.limit || 2;
        alert(`You have reached the maximum limit of ${limit} free course${limit !== 1 ? 's' : ''}. Please purchase a course to continue.`);
      } else {
        console.log("Error adding course:", error);
      }
    }
  };

  const closeTelegramModal = () => {
    setShowTelegramModal(false);
    setPendingFreeCourse(null);
  };

  const handleQuickStart = () => {
    handleCloseModal();
    navigate('/start-test');
  };

  const preferredJourneyOrder = [
    '/typing-display',
    '/start-test',
    EXAM_PREP_PATH,
    '/steno/practice',
    '/community',
    '/job-updates',
    '/blog',
  ];

  const recommendedJourneyLink = activeCourses.length > 0
    ? '/study'
    : isLoggedIn
      ? '/start-test'
      : '/typing-display';

  const orderedGuidedJourneys = guidedJourneys.filter(item => !item.featureKey || isFeatureEnabled(item.featureKey)).sort((a, b) => {
    if (recommendedJourneyLink && a.link === recommendedJourneyLink && b.link !== recommendedJourneyLink) {
      return -1;
    }
    if (recommendedJourneyLink && b.link === recommendedJourneyLink && a.link !== recommendedJourneyLink) {
      return 1;
    }

    const aIndex = preferredJourneyOrder.indexOf(a.link);
    const bIndex = preferredJourneyOrder.indexOf(b.link);

    return (aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex)
      - (bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex);
  });

  const repeatedGuidedJourneys = [...orderedGuidedJourneys, ...orderedGuidedJourneys];
  const activeFeatures = features.filter(feature => !feature.featureKey || isFeatureEnabled(feature.featureKey));
  const repeatedFeatures = [...activeFeatures, ...activeFeatures];

  const allExamGuidePages = Object.values(EXAM_LANDING_PAGES);
  const initialExamGuideCount = 16;
  const visibleExamGuidePages = showAllExamGuides
    ? allExamGuidePages
    : allExamGuidePages.slice(0, initialExamGuideCount);

  return (
    <PageWrapper>
      <Helmet>
        <title>Free Typing Test Practice | Online Typing Test for SSC CGL, CHSL, RRB | Typogram</title>
        <meta
          name="description"
          content="Free typing test and practice platform for SSC, CHSL, RRB, and Banking exams. Improve your typing speed (WPM) with AI-powered courses, real exam patterns, and instant results. Start practicing today!"
        />
        <meta name="keywords" content="typing test, typing speed test, free typing practice, SSC typing test, CHSL typing test, RRB typing test, banking typing test, typing speed, WPM test, typing practice online, government exam typing, typing courses, improve typing speed" />
        <link rel="canonical" href="https://typogram.in/" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://typogram.in/" />
        <meta property="og:title" content="Typogram - Master Typing for Exams & Typing Enthusiasts" />
        <meta property="og:description" content="Master typing for government exams (SSC, CHSL, RRB, Banking, UPSC) and improve your typing skills as a hobby. AI-powered courses, practice tests, typing games, and fun challenges for all typing enthusiasts." />
        <meta property="og:image" content="https://typogram.in/og-image.jpg" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Typogram - Master Typing for Exams & Typing Enthusiasts" />
        <meta name="twitter:description" content="Master typing for government exams and improve your typing skills as a hobby. AI-powered courses, practice tests, typing games, and fun challenges." />
        <meta name="twitter:image" content="https://typogram.in/og-image.jpg" />

        {/* Schema.org Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "@id": "https://typogram.in/#website",
            "name": "Typogram",
            "url": "https://typogram.in",
            "description": "AI-powered typing platform for mastering typing skills for competitive exams",
            "publisher": { "@id": "https://typogram.in/#organization" },
            "potentialAction": {
              "@type": "SearchAction",
              "target": {
                "@type": "EntryPoint",
                "urlTemplate": "https://typogram.in/blog?q={search_term_string}"
              },
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "@id": "https://typogram.in/#organization",
            "name": "Typogram",
            "url": "https://typogram.in",
            "logo": "https://typogram.in/logo512.png",
            "description": "Free online typing test and exam preparation platform for SSC, CHSL, RRB, banking, and stenography aspirants in India.",
            "sameAs": [
              "https://www.youtube.com/@typogram",
              "https://t.me/typogram"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Typogram",
            "applicationCategory": "EducationalApplication",
            "operatingSystem": "Web, Android, iOS",
            "url": "https://typogram.in",
            "description": "Free typing test, courses, and stenography practice for government exam preparation in India.",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "INR"
            },
            "publisher": { "@id": "https://typogram.in/#organization" }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Free Account",
                "url": "https://typogram.in/register"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Practice",
                "url": "https://typogram.in/typing-display"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "Demo Test",
                "url": "https://typogram.in/start-test"
              },
              {
                "@type": "ListItem",
                "position": 4,
                "name": "Exams",
                "url": "https://typogram.in/courses"
              },
              {
                "@type": "ListItem",
                "position": 5,
                "name": "Friends & Chat",
                "url": "https://typogram.in/friends"
              },
              {
                "@type": "ListItem",
                "position": 6,
                "name": "Typing Games",
                "url": "https://typogram.in/games"
              },
              {
                "@type": "ListItem",
                "position": 7,
                "name": "Daily Challenge",
                "url": "https://typogram.in/daily-challenge"
              },
              {
                "@type": "ListItem",
                "position": 8,
                "name": "Race Mode",
                "url": "https://typogram.in/race"
              },
              {
                "@type": "ListItem",
                "position": 9,
                "name": "Tournaments",
                "url": "https://typogram.in/tournaments"
              },
              {
                "@type": "ListItem",
                "position": 10,
                "name": "SSC CGL Typing Course",
                "url": "https://typogram.in/courses?category=SSC"
              },
              {
                "@type": "ListItem",
                "position": 11,
                "name": "CHSL Typing Test",
                "url": "https://typogram.in/courses?category=CHSL"
              },
              {
                "@type": "ListItem",
                "position": 12,
                "name": "RRB Typing Course",
                "url": "https://typogram.in/courses?category=RRB"
              },
              {
                "@type": "ListItem",
                "position": 13,
                "name": "Banking Typing Test",
                "url": "https://typogram.in/courses?category=Banking"
              }
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://typogram.in/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Courses",
                "item": "https://typogram.in/courses"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "Practice",
                "item": "https://typogram.in/typing-display"
              },
              {
                "@type": "ListItem",
                "position": 4,
                "name": "Blog",
                "item": "https://typogram.in/blog"
              }
            ]
          })}
        </script>
      </Helmet>

      <SubscriptionExtendBanner onExtended={fetchActiveCourses} />

      <main id="main-content">
        {/* HERO SECTION */}
        <HeroSection>

          <HeroContent
            data-tour="home-discovery-hero"
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            <LogoText
              initial={false}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              style={{ willChange: 'transform, opacity' }}
            >
              TYPOGRAM
            </LogoText>

            <HeroTagline
              initial={false}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
              style={{ willChange: 'opacity' }}
            >
              Master Typing Skills for <TypewriterText>{typingWords[currentWord]}</TypewriterText>
            </HeroTagline>
            <HeroDescription>Prepare for your typing exam, build everyday speed, or practise mock tests. Choose your goal and take the next step.</HeroDescription>

            <CTAButtonGroup
              data-tour="home-discovery-actions"
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              style={{ willChange: 'transform, opacity' }}
            >
              <CTAPrimaryRow>
                <CTAButton
                  $primary
                  onClick={handleGetStarted}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {!isLoggedIn && <Rocket size={18} style={{ marginRight: 8 }} />} {isLoggedIn ? 'Go to Dashboard' : 'Start Free'}
                </CTAButton>
                {isFeatureEnabled("study_schedule") && (
                  <CTAButton onClick={() => {trackMockFunnel("mock_discovery_click", {source:"homepage"}); navigate(EXAM_PREP_PATH);}} whileTap={{ scale: 0.98 }}>
                    <FiFileText size={18} style={{ marginRight: 8 }}/> Explore mock tests
                  </CTAButton>
                )}
              </CTAPrimaryRow>
              <HeroHelpLinks>
                {!isLoggedIn && <CTADemoLink type="button" onClick={handleTryDemo}>Try a free demo</CTADemoLink>}
                <CTADemoLink type="button" onClick={() => navigate('/video-guides')}>Watch video guides</CTADemoLink>
                <CTADemoLink type="button" onClick={() => navigate('/get-started')}>Help me choose</CTADemoLink>
              </HeroHelpLinks>
              <CTADemoLink type="button" onClick={handleLaunchTour}>Take a quick tour →</CTADemoLink>
            </CTAButtonGroup>
          </HeroContent>

          <ScrollIndicator
            initial={false}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <span>SCROLL TO EXPLORE</span>
            <span aria-hidden style={{ fontSize: "20px", lineHeight: 1 }}>↓</span>
          </ScrollIndicator>
        </HeroSection>

        {isFeatureEnabled("study_schedule") && <ExamPrepDiscovery />}

        <div data-tour="home-discovery-workflows">
          <HomeWorkflowShowcase />
        </div>

        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 20px" }}>
          <ExamTypingGuidesBlock
            title="Top Government Typing Exam Guides"
            subtitle="Go straight to the most searched typing skill-test pages for SSC CGL, SSC CHSL, RRB, Delhi Police, BSF Head Constable, Hindi typing, and free speed tests."
          />
        </div>

        <FeaturesSection style={{ minHeight: "auto", paddingTop: "4rem", paddingBottom: "4rem" }}>
          <FeaturesContainer>
            <SectionHeader
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <SectionTitle>Choose Your <span>Next Step</span></SectionTitle>
              <SectionSubtitle>
                Start from the flow you care about most right now: test practice, daily study planning, or live government job tracking.
              </SectionSubtitle>
            </SectionHeader>
            <JourneyCarousel
              style={{ "--journey-items": orderedGuidedJourneys.length }}
            >
              <JourneyTrack>
                {repeatedGuidedJourneys.map((item, index) => (
                  <JourneyStripCard
                    key={`${item.title}-${index}`}
                    type="button"
                    onClick={() => navigate(item.link)}
                    whileTap={{ scale: 0.985 }}
                  >
                    <JourneyStripIcon $color={item.color} $colorEnd={item.colorEnd} $shape={item.shape}>
                      {item.icon}
                    </JourneyStripIcon>
                    <JourneyStripMeta>{item.benefit}</JourneyStripMeta>
                    <JourneyStripTitle>{item.title}</JourneyStripTitle>
                    <JourneyStripDescription>{item.description}</JourneyStripDescription>
                    <JourneyStripCta>Open flow</JourneyStripCta>
                  </JourneyStripCard>
                ))}
              </JourneyTrack>
            </JourneyCarousel>
          </FeaturesContainer>
        </FeaturesSection>

        {/* FEATURES SECTION */}
        <FeaturesSection>
          <FeaturesContainer>
            <SectionHeader
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <SectionTitle>Why Choose <span>Typogram?</span></SectionTitle>
              <SectionSubtitle>
                Everything you need to master typing and ace your exams. Whether you're preparing for <a href="/courses?category=SSC" style={{ color: '#a855f7', textDecoration: 'none' }}>SSC typing tests</a>, <a href="/courses?category=RRB" style={{ color: '#a855f7', textDecoration: 'none' }}>Railway exams</a>, <a href="/courses?category=Banking" style={{ color: '#a855f7', textDecoration: 'none' }}>Banking typing tests</a>, or just want to improve your typing speed as a hobby, Typogram provides comprehensive tools and <a href="/typing-display" style={{ color: '#a855f7', textDecoration: 'none' }}>practice sessions</a> to help you succeed.
              </SectionSubtitle>
            </SectionHeader>

            <JourneyCarousel
              style={{ "--journey-items": activeFeatures.length || 1 }}
            >
              <JourneyTrack $reverse>
                {repeatedFeatures.map((feature, index) => (
                  <JourneyStripCard
                    key={`${feature.title}-${index}`}
                    type="button"
                    onClick={() => feature.link && navigate(feature.link)}
                    whileTap={{ scale: 0.985 }}
                    style={{ cursor: feature.link ? 'pointer' : 'default' }}
                  >
                    <JourneyStripIcon
                      $color={feature.color}
                      $colorEnd={feature.colorEnd}
                      $shape={feature.shape}
                    >
                      {feature.icon}
                    </JourneyStripIcon>
                    <JourneyStripMeta>{feature.benefit}</JourneyStripMeta>
                    <JourneyStripTitle>{feature.title}</JourneyStripTitle>
                    <JourneyStripDescription>{feature.description}</JourneyStripDescription>
                    <JourneyStripCta>{feature.link ? 'Explore feature' : 'Included in Typogram'}</JourneyStripCta>
                  </JourneyStripCard>
                ))}
              </JourneyTrack>
            </JourneyCarousel>
          </FeaturesContainer>
        </FeaturesSection>

        {/* COURSES SECTION */}
        <CoursesSection>
          <FeaturesContainer>
            <SectionHeader
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <SectionTitleRow>
                <SectionTitle>Explore <span>Courses</span></SectionTitle>
                {/* Expanding Search Bar */}
                <SearchBarContainer
                  className="search-box"
                  onSubmit={(e) => e.preventDefault()}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <SearchInput
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search courses..."
                    $hasValue={!!searchQuery}
                  />
                  <SearchClearButton
                    onClick={() => setSearchQuery('')}
                    type="reset"
                  />
                </SearchBarContainer>
              </SectionTitleRow>
              <SectionSubtitle>
                Choose your exam category and start your typing journey
              </SectionSubtitle>
            </SectionHeader>

            <CoursesWrapper>
              {coursesLoading ? (
                <CoursesLoading>Loading courses...</CoursesLoading>
              ) : (
                <>

                  {/* Dynamic Category Pills */}
                  <CategoryScrollContainer>
                    {categories.map((category) => (
                      <CategoryPill
                        key={category}
                        $active={selectedCategory === category}
                        onClick={() => handleCategorySelect(category)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {category}
                      </CategoryPill>
                    ))}
                  </CategoryScrollContainer>

                  {/* Canvas-style Card Grid */}
                  <CourseCardGrid>
                    {getFilteredCourses().length > 0 ? (
                      getFilteredCourses().slice(0, 6).map((course, index) => {
                        const gradientColors = [
                          { start: 'rgb(253,137,68)', end: 'rgb(153,75,23)' }, // Orange
                          { start: '#D34F48', end: '#772522' }, // Red
                          { start: '#8ed500', end: '#5a8a00' }, // Green
                          { start: '#a855f7', end: '#6d28d9' }, // Purple
                          { start: '#00d4aa', end: '#009970' }, // Teal
                          { start: '#ff9f43', end: '#cc7f35' }, // Orange-Yellow
                        ];
                        const backgroundGradients = [
                          'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', // Purple
                          'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', // Pink-Red
                          'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', // Blue-Cyan
                          'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', // Green-Teal
                          'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', // Pink-Yellow
                          'linear-gradient(135deg, #30cfd0 0%, #330867 100%)', // Cyan-Purple
                        ];
                        const gradient = gradientColors[index % gradientColors.length];
                        const bgGradient = backgroundGradients[index % backgroundGradients.length];
                        const isLeft = index % 2 === 0;
                        const titleWords = (course.title || course.subject || 'Course').split(' ');
                        const firstWord = titleWords[0] || 'Course';
                        const secondWord = titleWords[1] || '';
                        const category = course.category || 'Course';

                        return (
                          <CourseItem
                            key={course.id}
                            href="#"
                            $bgColor={bgGradient}
                            onClick={(e) => {
                              e.preventDefault();
                              handleCourseClick(course);
                            }}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                          >
                            {/* Animated SVG Border */}
                            <CanvasBorder>
                              <svg>
                                <defs>
                                  <linearGradient id={`grad-${course.id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor={gradient.start} stopOpacity="1" />
                                    <stop offset="100%" stopColor={gradient.end} stopOpacity="1" />
                                  </linearGradient>
                                </defs>
                                <RectGradient
                                  fill="none"
                                  stroke={`url(#grad-${course.id})`}
                                  x="0"
                                  y="0"
                                  width="100%"
                                  height="100%"
                                />
                              </svg>
                            </CanvasBorder>

                            {/* Image Wrapper */}
                            <CanvasImgWrapper>
                              {course.image ? (
                                <CanvasImg
                                  src={course.image}
                                  alt={`${course.title || course.subject} typing course for ${course.category || 'government exam'} preparation - Typogram`}
                                  loading="lazy"
                                  width="170"
                                  height="170"
                                />
                              ) : (
                                <CanvasImg
                                  src="https://via.placeholder.com/200x200/8b5cf6/ffffff?text=Course"
                                  alt={`${course.title || course.subject} typing course - Typogram`}
                                  loading="lazy"
                                  width="170"
                                  height="170"
                                />
                              )}
                            </CanvasImgWrapper>

                            {/* Text Content */}
                            <CanvasCopy isLeft={isLeft}>
                              <CanvasCopySubtitle>{category}</CanvasCopySubtitle>
                              <CanvasCopyTitle>{firstWord}</CanvasCopyTitle>
                              {secondWord && <CanvasCopyTitle>{secondWord}</CanvasCopyTitle>}
                              <CanvasCopyDetails>
                                {course.description
                                  ? (course.description.length > 30
                                    ? course.description.substring(0, 30) + '...'
                                    : course.description)
                                  : 'Master typing skills'}
                              </CanvasCopyDetails>
                            </CanvasCopy>
                            <CourseCardHover aria-hidden>
                              <span>Open course</span>
                            </CourseCardHover>
                          </CourseItem>
                        );
                      })
                    ) : (
                      <CoursesLoading>No courses available in this category.</CoursesLoading>
                    )}
                  </CourseCardGrid>

                  {getFilteredCourses().length > 6 && (
                    <ViewAllButton
                      type="button"
                      onClick={() => navigate('/courses')}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      View All Courses <span aria-hidden style={{ transform: 'rotate(-90deg)', display: 'inline-block' }}>↓</span>
                    </ViewAllButton>
                  )}
                </>
              )}
            </CoursesWrapper>
          </FeaturesContainer>
        </CoursesSection>

        {/* STATS SECTION */}
        <StatsSection>
          <StatsContainer>
            {stats.map((stat, index) => (
              <StatItem
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <StatNumber>{stat.number}</StatNumber>
                <StatLabel>{stat.label}</StatLabel>
              </StatItem>
            ))}
          </StatsContainer>
        </StatsSection>

        {/* TESTIMONIALS SECTION */}
        <TestimonialsSection>
          <FeaturesContainer>
            <SectionHeader
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <SectionTitle>Loved by <span>Thousands</span></SectionTitle>
              <SectionSubtitle>
                See what our learners say about their journey
              </SectionSubtitle>
            </SectionHeader>

            {feedbacksLoading ? (
              <div style={{ textAlign: 'center', padding: '4rem', color: 'rgba(255,255,255,0.5)' }}>
                Loading testimonials...
              </div>
            ) : feedbacks.length > 0 ? (
              <TestimonialsGrid>
                {feedbacks.map((feedback, index) => (
                  <TestimonialCard
                    key={feedback.id}
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: index * 0.1 }}
                  >
                    <QuoteIcon><span aria-hidden>❝</span></QuoteIcon>
                    {feedback.rating && (
                      <StarRating>
                        {[...Array(5)].map((_, i) => (
                          <span key={i} aria-hidden style={{ color: i < feedback.rating ? '#fbbf24' : 'rgba(255,255,255,0.2)' }}>★</span>
                        ))}
                      </StarRating>
                    )}
                    <TestimonialText>
                      "{feedback.message}"
                    </TestimonialText>
                    <TestimonialAuthor>
                      <AuthorAvatar>
                        {feedback.name
                          .split(' ')
                          .map(n => n[0])
                          .join('')
                          .toUpperCase()
                          .slice(0, 2)}
                      </AuthorAvatar>
                      <AuthorInfo>
                        <div className="name">{feedback.name}</div>
                        {feedback.email && (
                          <div className="role">{feedback.email}</div>
                        )}
                      </AuthorInfo>
                    </TestimonialAuthor>
                  </TestimonialCard>
                ))}
              </TestimonialsGrid>
            ) : (
              <TestimonialCard
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <QuoteIcon><span aria-hidden>❝</span></QuoteIcon>
                <StarRating>
                  {[...Array(5)].map((_, i) => <span key={i} aria-hidden>★</span>)}
                </StarRating>
                <TestimonialText>
                  "Typogram helped me clear my SSC typing test with 45 WPM! The AI-powered practice sessions and real exam patterns made all the difference. Highly recommended for anyone serious about government exams."
                </TestimonialText>
                <TestimonialAuthor>
                  <AuthorAvatar>P</AuthorAvatar>
                  <AuthorInfo>
                    <div className="name">Priya Sharma</div>
                    <div className="role">SSC CGL Qualified • Delhi</div>
                  </AuthorInfo>
                </TestimonialAuthor>
              </TestimonialCard>
            )}
          </FeaturesContainer>

          {/* Review Schema for Testimonials */}
          {feedbacks.length > 0 && (
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Organization",
                "name": "Typogram",
                "aggregateRating": {
                  "@type": "AggregateRating",
                  "ratingValue": "4.8",
                  "reviewCount": feedbacks.length,
                  "bestRating": "5",
                  "worstRating": "1"
                },
                "review": feedbacks.slice(0, 5).map(feedback => ({
                  "@type": "Review",
                  "author": {
                    "@type": "Person",
                    "name": feedback.name || "Anonymous"
                  },
                  "reviewRating": {
                    "@type": "Rating",
                    "ratingValue": feedback.rating || 5,
                    "bestRating": "5",
                    "worstRating": "1"
                  },
                  "reviewBody": feedback.message
                }))
              })}
            </script>
          )}
        </TestimonialsSection>

        {/* EXAM TYPING GUIDES — SEO internal linking */}
        <ExamGuidesSection>
          <FeaturesContainer>
            <SectionHeader
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <SectionTitle>Government Exam <span>Typing Guides</span></SectionTitle>
              <SectionSubtitle>
                Free typing test guides for SSC, Railway, Banking, Hindi typing, and more — with WPM targets, exam patterns, and practice tips.
              </SectionSubtitle>
            </SectionHeader>
            <ExamGuidesGrid>
              {visibleExamGuidePages.map((page) => (
                <ExamGuideLink key={page.slug} href={`/${page.slug}`}>
                  <ExamGuideTitle>{page.exam}</ExamGuideTitle>
                  <ExamGuideMeta>Typing guide</ExamGuideMeta>
                </ExamGuideLink>
              ))}
            </ExamGuidesGrid>
            <ExamGuidesActions>
              {allExamGuidePages.length > initialExamGuideCount && (
                <ExamGuidesToggleButton
                  type="button"
                  onClick={() => setShowAllExamGuides((s) => !s)}
                >
                  {showAllExamGuides
                    ? "Show fewer guides"
                    : `View all ${allExamGuidePages.length} guides`}
                </ExamGuidesToggleButton>
              )}
              <ExamGuidesHubLink href="/typing-exam-guides">
                Exam guides hub
              </ExamGuidesHubLink>
              <ExamGuidesHubLink href="/all-state-typing-tests">
                All state typing tests
              </ExamGuidesHubLink>
              <ExamGuidesHubLink href="/job-updates">
                Job notices + typing prep
              </ExamGuidesHubLink>
            </ExamGuidesActions>
          </FeaturesContainer>
        </ExamGuidesSection>

        {/* FAQ SECTION */}
        <FAQSection>
          <FAQContainer>
            <SectionHeader
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <SectionTitle>Frequently Asked <span>Questions</span></SectionTitle>
              <SectionSubtitle>
                Everything you need to know about Typogram
              </SectionSubtitle>
            </SectionHeader>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <FAQAccordion faqs={[
                {
                  q: "What typing exams does Typogram prepare me for?",
                  a: "Typogram offers comprehensive preparation for all major government typing exams including SSC CGL, SSC CHSL, RRB (Railway), Banking (IBPS, SBI), UPSC, State PSC exams, and more. We also cater to typing enthusiasts who want to improve their skills."
                },
                {
                  q: "Do I need to pay to use Typogram?",
                  a: "No! Typogram offers a free account with access to practice sessions, demo tests, and basic features. You can <a href='/register'>create a free account</a> and start practicing immediately. Premium courses are available for advanced exam-specific preparation."
                },
                {
                  q: "How does Typogram help improve typing speed?",
                  a: "Typogram uses AI-powered analysis to track your progress, identify weak areas, and provide personalized practice recommendations. Our <a href='/typing-display'>practice mode</a> offers real-time feedback, and our <a href='/daily-challenge'>daily challenges</a> keep you motivated."
                },
                {
                  q: "Can I practice typing games on Typogram?",
                  a: "Yes! Typogram offers various <a href='/games'>typing games</a> including Word Blaster, Typing Ninja, Code Runner, and more. These games make learning fun while improving your speed and accuracy."
                },
                {
                  q: "What is the difference between Practice and Demo Test?",
                  a: "Practice mode (<a href='/typing-display'>/typing-display</a>) is for unlimited practice sessions with real-time feedback. Demo Test (<a href='/start-test'>/start-test</a>) simulates actual exam conditions with time limits and official evaluation criteria."
                },
                {
                  q: "How accurate is the typing evaluation?",
                  a: "Our evaluation system follows official exam guidelines (SSC, Railway, etc.) and calculates mistakes, accuracy, and speed precisely. We use character-level analysis and exam-specific calculators to ensure accurate results."
                }
              ]} />

              {/* FAQPage Schema for SEO */}
              <script type="application/ld+json">
                {JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  "mainEntity": [
                    {
                      "@type": "Question",
                      "name": "What typing exams does Typogram prepare me for?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Typogram offers comprehensive preparation for all major government typing exams including SSC CGL, SSC CHSL, RRB (Railway), Banking (IBPS, SBI), UPSC, State PSC exams, and more. We also cater to typing enthusiasts who want to improve their skills."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Do I need to pay to use Typogram?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "No! Typogram offers a free account with access to practice sessions, demo tests, and basic features. You can create a free account and start practicing immediately. Premium courses are available for advanced exam-specific preparation."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "How does Typogram help improve typing speed?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Typogram uses AI-powered analysis to track your progress, identify weak areas, and provide personalized practice recommendations. Our practice mode offers real-time feedback, and our daily challenges keep you motivated."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "Can I practice typing games on Typogram?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Yes! Typogram offers various typing games including Word Blaster, Typing Ninja, Code Runner, and more. These games make learning fun while improving your speed and accuracy."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "What is the difference between Practice and Demo Test?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Practice mode is for unlimited practice sessions with real-time feedback. Demo Test simulates actual exam conditions with time limits and official evaluation criteria."
                      }
                    },
                    {
                      "@type": "Question",
                      "name": "How accurate is the typing evaluation?",
                      "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Our evaluation system follows official exam guidelines (SSC, Railway, etc.) and calculates mistakes, accuracy, and speed precisely. We use character-level analysis and exam-specific calculators to ensure accurate results."
                      }
                    }
                  ]
                })}
              </script>
            </motion.div>
          </FAQContainer>
        </FAQSection>

        {/* CTA SECTION */}
        <CTASection>
          <CTAContent
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <CTATitle>Everything You Need to <span style={{ color: '#a855f7' }}>Succeed</span></CTATitle>
            <CTASubtitle>
              Join 50K+ learners improving their typing today — free to start, premium courses when you&apos;re ready for exam prep.
            </CTASubtitle>
            <CTAButtonGroup>
              <CTAButton
                $primary
                onClick={handleGetStarted}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {!isLoggedIn && <Rocket size={18} style={{ marginRight: 8 }} />} {isLoggedIn ? 'Go to Dashboard' : 'Start Free Now'}
              </CTAButton>
              {!isLoggedIn && (
                <CTADemoLink type="button" onClick={handleTryDemo}>
                  Or try a demo test first
                </CTADemoLink>
              )}
            </CTAButtonGroup>
            <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: 'rgba(255,255,255,0.55)' }}>
              <a href="/typing-test/pricing" style={{ color: '#c4b5fd', textDecoration: 'none', fontWeight: 600 }}>
                View pricing &amp; test passes →
              </a>
            </p>

            {/* Social Sharing */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
              style={{ marginTop: '2rem', textAlign: 'center' }}
            >
              <p style={{ color: 'rgba(255, 255, 255, 0.6)', marginBottom: '1rem', fontSize: '0.9rem' }}>
                Share Typogram with friends
              </p>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent('https://typogram.in')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: 'rgba(24, 119, 242, 0.1)',
                    border: '1px solid rgba(24, 119, 242, 0.3)',
                    borderRadius: '50px',
                    color: '#1877F2',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.3s ease',
                    fontSize: '0.9rem'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(24, 119, 242, 0.2)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(24, 119, 242, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <span aria-hidden>f</span> Facebook
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent('https://typogram.in')}&text=${encodeURIComponent('Master typing skills for government exams with Typogram!')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: 'rgba(29, 161, 242, 0.1)',
                    border: '1px solid rgba(29, 161, 242, 0.3)',
                    borderRadius: '50px',
                    color: '#1DA1F2',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.3s ease',
                    fontSize: '0.9rem'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(29, 161, 242, 0.2)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(29, 161, 242, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <span aria-hidden>X</span> Twitter
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent('https://typogram.in')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: 'rgba(10, 102, 194, 0.1)',
                    border: '1px solid rgba(10, 102, 194, 0.3)',
                    borderRadius: '50px',
                    color: '#0A66C2',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.3s ease',
                    fontSize: '0.9rem'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(10, 102, 194, 0.2)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(10, 102, 194, 0.1)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <span aria-hidden>in</span> LinkedIn
                </a>
              </div>
            </motion.div>
          </CTAContent>
        </CTASection>

        {/* YOUTUBE VIDEOS SECTION */}
        <Suspense fallback={null}>
          <YouTubeVideos />
        </Suspense>
      </main>

      {/* FOOTER */}
      <Footer>
        <InstituteFooterBanner>
          <InstituteBannerTitle>Run a Coaching Institute? <span>Go Digital</span></InstituteBannerTitle>
          <InstituteBannerDesc>Get a fully white-labeled typing platform for your students with automated evaluations and batch management.</InstituteBannerDesc>
          <InstituteBannerCTA href="/for-institutes">Partner With Us</InstituteBannerCTA>
        </InstituteFooterBanner>

        <FooterContent>
          <FooterColumn>
            <FooterTitle>Typogram</FooterTitle>
            <FooterText>
              Your AI-powered platform for mastering typing skills.
              Perfect for government exam preparation and typing enthusiasts who want to improve their speed and accuracy.
            </FooterText>
          </FooterColumn>

          <FooterColumn>
            <FooterTitle>Quick Links</FooterTitle>
            <FooterLink href="/register">Free Account</FooterLink>
            <FooterLink href="/courses">All Courses</FooterLink>
            <FooterLink href="/about">About Us</FooterLink>
            <FooterLink href="/leaderboard">Leaderboards</FooterLink>
            <FooterLink href="/blog">Blog</FooterLink>
            <FooterLink href="/contact-us">Contact</FooterLink>
          </FooterColumn>

          <FooterColumn>
            <FooterTitle>Exam Preparation</FooterTitle>
            <FooterSectionNote>
              Start with these top exam guides. Browse complete state-wise coverage from the hubs below.
            </FooterSectionNote>
            <FooterLink href="/ssc-cgl-typing-test">SSC CGL Typing Test</FooterLink>
            <FooterLink href="/ssc-chsl-typing-test">SSC CHSL Typing Test</FooterLink>
            <FooterLink href="/rrb-typing-test">RRB Typing Test</FooterLink>
            <FooterLink href="/ibps-typing-test">IBPS Typing Test</FooterLink>
            <FooterLink href="/banking-typing-test">Banking Typing Test</FooterLink>
            <FooterLink href="/typing-speed-test">Typing Speed Test</FooterLink>
            <FooterLink href="/typing-display">Free Typing Test</FooterLink>
            <FooterLink href="/typing-exam-guides">All Exam Typing Guides →</FooterLink>
            <FooterLink href="/all-state-typing-tests">All State Typing Tests →</FooterLink>
          </FooterColumn>

          <FooterColumn>
            <FooterTitle>Features</FooterTitle>
            <FooterLink href="/typing-display">Practice</FooterLink>
            <FooterLink href="/start-test">Demo Test</FooterLink>
            <FooterLink href="/friends">Friends & Chat</FooterLink>
            <FooterLink href="/games">Typing Games</FooterLink>
            <FooterLink href="/race">Race Mode</FooterLink>
            <FooterLink href="/daily-challenge">Daily Challenge</FooterLink>
            <FooterLink href="/tournaments">Tournaments</FooterLink>
            <FooterLink href="/achievements">Achievements</FooterLink>
          </FooterColumn>

          <FooterColumn>
            <FooterTitle>Support</FooterTitle>
            <FooterText>Email: support@typogram.in</FooterText>
            <FooterText>Phone: +91-8077605057 </FooterText>
            <FooterText style={{ marginBottom: '1rem' }}>Available 24/7 for support</FooterText>
            <FooterLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setShowFeedbackForm(true);
              }}
              style={{ cursor: 'pointer' }}
            >
              Share Your Feedback →
            </FooterLink>
            <FooterLink href="/faq">FAQ</FooterLink>
            <FooterLink href="/help">Help Center</FooterLink>
          </FooterColumn>

          <FooterColumn>
            <FooterTitle>Legal</FooterTitle>
            <FooterLink href="/privacy-policy">Privacy Policy</FooterLink>
            <FooterLink href="/terms-of-service">Terms of Service</FooterLink>
            <FooterLink href="/refund-policy">Refund Policy</FooterLink>
            <FooterLink href="/cookie-policy">Cookie Policy</FooterLink>
          </FooterColumn>

          <FooterColumn>
            <FooterTitle>Newsletter</FooterTitle>
            <FooterText>Stay updated with our latest features, tips, and exam updates.</FooterText>
            <NewsletterSection>
              <form onSubmit={handleNewsletterSubscribe}>
                <NewsletterInput
                  type="email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  disabled={newsletterLoading}
                />
                <NewsletterButton type="submit" disabled={newsletterLoading}>
                  {newsletterLoading ? 'Subscribing...' : 'Subscribe'}
                </NewsletterButton>
                {newsletterMessage && (
                  <NewsletterMessage success={newsletterMessage.includes('Successfully')}>
                    {newsletterMessage}
                  </NewsletterMessage>
                )}
              </form>
            </NewsletterSection>
          </FooterColumn>
        </FooterContent>

        <FooterSocialStrip>
          <FooterSocialTop>
            {TYPOGRAM_SOCIAL.map(({ href, label, icon, hover }) => (
              <FooterBrandIcon
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                $hoverColor={hover}
              >
                <span aria-hidden>{icon}</span>
              </FooterBrandIcon>
            ))}
          </FooterSocialTop>
        </FooterSocialStrip>

        <FooterBottom>
          <p>© 2025 Typogram. All rights reserved.</p>
          <p style={{ marginTop: "0.65rem" }}>
            Made with <span className="heart-beat">❤️</span> for India — follow Typogram on the networks above.
          </p>
        </FooterBottom>
      </Footer>

      {/* QUICK-VIEW MODAL */}
      <AnimatePresence>
        {selectedCourse && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseModal}
          >
            <ModalPanel
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalCloseButton
                onClick={handleCloseModal}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                aria-label="Close course details"
              >
                <span aria-hidden>×</span>
              </ModalCloseButton>

              <ModalContent>
                <ModalHeader>
                  <ModalCourseImage>
                    {selectedCourse.image ? (
                      <img src={selectedCourse.image} alt={selectedCourse.title} loading="lazy" decoding="async" />
                    ) : (
                      <span style={{ fontSize: '2.5rem' }}>
                        {categoryIcons[selectedCourse.category] || '📚'}
                      </span>
                    )}
                  </ModalCourseImage>
                  <ModalCourseInfo>
                    <ModalCourseTitle>{selectedCourse.title}</ModalCourseTitle>
                    <ModalCourseCategory>
                      {categoryIcons[selectedCourse.category] || '📚'} {selectedCourse.category}
                    </ModalCourseCategory>
                    <ModalCourseDesc>{selectedCourse.description}</ModalCourseDesc>
                  </ModalCourseInfo>
                </ModalHeader>

                {/* Benefits Section */}
                <BenefitsSection>
                  <BenefitsTitle>
                    <span aria-hidden>🏆</span> Why Choose This Course?
                  </BenefitsTitle>
                  <BenefitsList>
                    {(selectedCourse.benefits && selectedCourse.benefits.length > 0
                      ? selectedCourse.benefits
                      : ['Real Exam Interface', 'Instant Accuracy Reports', '2024-25 Updated Content', 'AI-Powered Analysis']
                    ).map((benefit, idx) => (
                      <BenefitItem key={`benefit-${idx}`}>
                        <div className="icon"><span aria-hidden>✓</span></div>
                        <div className="text">{benefit}</div>
                      </BenefitItem>
                    ))}
                  </BenefitsList>
                </BenefitsSection>

                {/* Typing Courses */}
                <TypingCoursesSection>
                  <TypingCoursesSectionTitle>
                    Available <span>Courses</span>
                  </TypingCoursesSectionTitle>

                  {modalLoading ? (
                    <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.6)', padding: '2rem' }}>
                      Loading courses...
                    </div>
                  ) : typingCourses.length > 0 ? (
                    <TypingCoursesList>
                      {typingCourses.map((tc, idx) => {
                        const isActive = activeCourses.some(ac => ac.id === tc.id);
                        const isFree = tc.price === 0;

                        return (
                          <TypingCourseItem
                            key={`modal-course-${idx}`}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.1 }}
                          >
                            <TypingCourseItemInfo>
                              <TypingCourseItemTitle>{tc.subject}</TypingCourseItemTitle>
                              <TypingCourseItemMeta>
                                <span><span aria-hidden>⏱</span> {tc.validity}</span>
                                <span><span aria-hidden>🎓</span> {tc.tests}</span>
                              </TypingCourseItemMeta>
                              {(tc.rulesText || tc.rulesPdfUrl) && (
                                <TypingCourseRulesLink
                                  type="button"
                                  onClick={() => setRulesModalTypingCourse(tc)}
                                >
                                  <span aria-hidden style={{ fontSize: "12px" }}>i</span>
                                  Rules &amp; official PDF
                                </TypingCourseRulesLink>
                              )}
                            </TypingCourseItemInfo>

                            <TypingCourseItemPrice>
                              {isFree ? 'FREE' : `₹${tc.price}`}
                            </TypingCourseItemPrice>

                            <TypingCourseItemButton
                              onClick={() => handleAddToActiveCourses(tc)}
                              disabled={isActive}
                              isFree={isFree}
                              whileHover={{ scale: isActive ? 1 : 1.02 }}
                              whileTap={{ scale: isActive ? 1 : 0.98 }}
                            >
                              {isActive
                                ? 'Subscribed ✓'
                                : isFree
                                  ? 'Add Free'
                                  : 'Buy Now'}
                            </TypingCourseItemButton>
                          </TypingCourseItem>
                        );
                      })}
                    </TypingCoursesList>
                  ) : (
                    <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', padding: '2rem' }}>
                      No courses available yet.
                    </div>
                  )}
                </TypingCoursesSection>

                {/* Quick Start CTA */}
                {isLoggedIn && activeCourses.length > 0 && (
                  <QuickStartCTA
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <QuickStartText>
                      Ready to practice? Jump into your subscribed courses!
                    </QuickStartText>
                    <QuickStartButton
                      onClick={handleQuickStart}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Rocket size={18} style={{ marginRight: 8 }} />
                      Start Practice Now
                    </QuickStartButton>
                  </QuickStartCTA>
                )}
              </ModalContent>
            </ModalPanel>
          </ModalOverlay>
        )}
      </AnimatePresence>

      <Suspense fallback={null}>
        <TypingCourseRulesModal
          typingCourse={rulesModalTypingCourse}
          isOpen={!!rulesModalTypingCourse}
          onClose={() => setRulesModalTypingCourse(null)}
        />
      </Suspense>

      {/* Feedback Form Modal */}
      <Suspense fallback={null}>
        <FeedbackForm
          isOpen={showFeedbackForm}
          onClose={() => setShowFeedbackForm(false)}
        />
        <TelegramJoinModal
          isOpen={showTelegramModal}
          onClose={closeTelegramModal}
          onContinue={enrollPendingFreeCourse}
        />
      </Suspense>
    </PageWrapper>
  );
};

export default HomeComponent;
